import { toast } from 'sonner';

import {
  ApiError,
  CreateAegisManagementTaskRequest,
  TaskResponse,
} from '@/hooks/useAegisManagement';
import { Agent } from '@/hooks/useAgents';
import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import type { UseUserManagementReturn } from '@/sections/agents/types/hooks.types';
import { useMutation } from '@/utils/api';

/**
 * Domain-specific hook for managing Linux user operations
 *
 * Provides focused functionality for user creation and deletion,
 * with proper validation and error handling.
 *
 * @param enabled - Whether the hook should be active (useful for modal state)
 */
export const useUserManagement = (enabled = true): UseUserManagementReturn => {
  const axios = useAxios();

  // Helper function to validate username format
  const isValidUsername = (username: string): boolean => {
    const usernamePattern = /^[a-zA-Z]+\.[a-zA-Z]+$/;
    return usernamePattern.test(username);
  };

  // Get current users from agent health check data
  const getCurrentUsers = (agent?: Agent): string[] => {
    const authorizedUsersStr =
      agent?.health_check?.cloudflared_status?.authorized_users || '';
    return authorizedUsersStr
      ? authorizedUsersStr
          .split(',')
          .map(u => u.trim())
          .filter(u => u.length > 0) // Show all users, not just firstname.lastname format
      : [];
  };

  // Create task mutation for user management
  const createTaskMutation = useMutation<
    TaskResponse,
    ApiError,
    CreateAegisManagementTaskRequest
  >({
    defaultErrorMessage: 'Failed to create user task',
    mutationFn: async (
      task: CreateAegisManagementTaskRequest
    ): Promise<TaskResponse> => {
      const promise = axios.post('/aegis/management/tasks', task);

      const operationType = task.aegisManagementCapability.includes('adduser')
        ? 'add'
        : 'delete';

      toast.promise(promise, {
        loading: `${operationType === 'add' ? 'Creating' : 'Deleting'} user task...`,
        success: response => {
          const data = response.data as TaskResponse;
          return `User ${operationType === 'add' ? 'creation' : 'deletion'} task queued: ${data.taskId}`;
        },
        error: (err: ApiError) => {
          switch (err.type) {
            case 'network':
              return (
                err.message || 'Network error occurred while creating user task'
              );
            case 'validation':
              return (
                err.response?.data?.message || 'Invalid user data provided'
              );
            case 'server':
              return (
                err.response?.data?.message ||
                'Server error occurred while creating user task'
              );
            default:
              return (
                err.response?.data?.message ||
                err.message ||
                'Failed to create user task'
              );
          }
        },
      });

      const response = await promise;
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both tasks and agents to ensure table updates
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getAegisManagementTasks(),
      });
    },
  });

  // Add user function
  const addUser = async (
    aegisClientId: string,
    username: string
  ): Promise<TaskResponse> => {
    // Validate username format (first.last)
    if (!isValidUsername(username)) {
      toast.error(
        'The user must have a Praetorian email address. The Linux username must be the same as the username in it (e.g., john.doe)'
      );
      throw new Error('Invalid username format');
    }

    return createTaskMutation.mutateAsync({
      aegisManagementCapability: 'linux-system-adduser',
      aegisClientId,
      parameters: {
        username,
      },
    });
  };

  // Delete user function (confirmation handled by UI)
  const deleteUser = async (
    aegisClientId: string,
    username: string,
    _agentHostname?: string
  ): Promise<TaskResponse> => {
    return createTaskMutation.mutateAsync({
      aegisManagementCapability: 'linux-system-deluser',
      aegisClientId,
      parameters: {
        username,
        remove_home: '',
      },
    });
  };

  return {
    addUser,
    deleteUser,
    getCurrentUsers,
    isValidUsername,
    isLoading: createTaskMutation.isPending,
    enabled,
  };
};
